import Seo from "@/components/Seo";
import { PageHero, SectionHead, CTABand, Reveal, TickList } from "@/components/ui-bits";
import { TERRACES_IMAGE, DHIRAJ_PHOTO, COMPANY, PHONE_DISPLAY, EMAIL, ADDRESS, DHIRAJ_PHOTO as _d } from "@/lib/site";
import { useSettings } from "@/hooks/useContent";
import { Phone, Mail } from "lucide-react";

export default function About() {
  const { data: settings } = useSettings();
  const photo = settings?.key_person_photo || DHIRAJ_PHOTO;

  return (
    <>
      <Seo
        title="About Orvella Overseas — India-Based Food & Agricultural Products Exporter"
        description="Orvella Overseas Private Limited is an India-based B2B food and agricultural products export company connecting international buyers with quality products sourced from India."
        path="/about"
      />

      <PageHero
        eyebrow="About Us"
        title="About Orvella Overseas"
        sub="Orvella Overseas Private Limited is an India-based B2B food and agricultural products export company focused on connecting international buyers with quality products sourced from India."
      />

      <section className="section-pad">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-xl border border-line">
              <img src={TERRACES_IMAGE} alt="Farmers tending terraced agricultural fields in India" loading="lazy" className="h-auto w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="eyebrow">Our Approach</span>
            <span className="mt-3 block h-[2px] w-12 bg-gold" />
            <h2 className="mt-5 font-serif text-3xl font-semibold text-navy">Sourcing from India, Built Around Buyer Requirements</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-mist">
              Our approach is built around understanding buyer requirements, sourcing suitable products, maintaining clear
              communication and supporting the export process from enquiry to shipment.
            </p>
            <TickList
              className="mt-6"
              items={[
                "Understanding buyer requirements before proposing products",
                "Sourcing suitable products with agreed specifications",
                "Clear communication throughout the enquiry and order process",
                "Supporting packaging, documentation and shipment coordination as applicable",
              ]}
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-sand section-pad" data-testid="mission-vision-section">
        <div className="container-x grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="card h-full p-8">
              <span className="eyebrow">Our Mission</span>
              <span className="mt-3 block h-[2px] w-10 bg-gold" />
              <h3 className="mt-5 font-serif text-2xl font-semibold text-navy">Connecting Global Buyers with Dependable Sourcing</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">
                To connect global buyers with dependable sourcing opportunities from India.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="card h-full p-8">
              <span className="eyebrow">Our Vision</span>
              <span className="mt-3 block h-[2px] w-10 bg-gold" />
              <h3 className="mt-5 font-serif text-2xl font-semibold text-navy">Long-Term International Relationships</h3>
              <p className="mt-3 text-sm leading-relaxed text-mist">
                To build long-term international business relationships through quality, professionalism and transparent communication.
              </p>
            </div>
          </Reveal>
        </div>
        <div className="container-x mt-8">
          <Reveal>
            <div className="card p-8">
              <span className="eyebrow">Our Values</span>
              <span className="mt-3 block h-[2px] w-10 bg-gold" />
              <div className="mt-5 flex flex-wrap gap-3">
                {["Quality", "Integrity", "Reliability", "Responsiveness", "Long-Term Relationships"].map((v) => (
                  <span key={v} className="chip !px-4 !py-2 !text-sm">{v}</span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad" data-testid="export-contact-section">
        <div className="container-x grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <div className="mx-auto max-w-sm rounded-xl border-2 border-gold/60 bg-white p-3 shadow-[0_24px_50px_-30px_rgba(11,25,44,0.4)]">
              <img src={photo} alt={`${settings?.key_person_name || "Dhiraj Kumar"} — export contact at Orvella Overseas`} loading="lazy" className="aspect-[4/5] w-full rounded-lg object-cover object-top" />
              <div className="px-2 py-4 text-center">
                <h3 className="font-serif text-xl font-semibold text-navy">{settings?.key_person_name || "Dhiraj Kumar"}</h3>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-gold-dark">Export Contact · {settings?.key_person_title || "[TO BE UPDATED]"}</p>
                <p className="mt-2 text-sm text-mist">Mobile / WhatsApp: {settings?.key_person_phone || "+91 91993 19199"}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="eyebrow">Meet Our Export Contact</span>
            <span className="mt-3 block h-[2px] w-12 bg-gold" />
            <h2 className="mt-5 font-serif text-3xl font-semibold text-navy">A Direct Line to Our Export Team</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-mist">
              International buyers dealing with {COMPANY} are supported directly by {settings?.key_person_name || "Dhiraj Kumar"}.
              Share your product requirement and our team will respond with availability, specifications and a commercial quotation.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <a href={`tel:${(settings?.key_person_phone || "+91919319199").replace(/\s/g, "")}`} className="inline-flex items-center gap-2 font-semibold text-navy hover:text-gold-dark">
                <Phone size={16} className="text-gold-dark" /> {settings?.key_person_phone || "+91 91993 19199"}
              </a>
              <a href={`mailto:${EMAIL}`} className="inline-flex items-center gap-2 font-semibold text-navy hover:text-gold-dark">
                <Mail size={16} className="text-gold-dark" /> {EMAIL}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
